import { useState, useMemo } from "react";
import useAuthStore, {
  useCurrentTeam,
  useCurrentTeamId,
  useTeams,
  usePermissions,
  useRoles,
  useOrganizationRole,
  useIsOnboarded,
  useTeamsLoading,
  usePermissionsLoading,
  useHasHydrated,
} from "@/stores/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  Copy,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Shield,
  Users,
  Key,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "react-hot-toast";

export function RBACDebugTab() {
  const [permissionSearch, setPermissionSearch] = useState("");

  // Get all RBAC state
  const currentTeam = useCurrentTeam();
  const currentTeamId = useCurrentTeamId();
  const teams = useTeams();
  const permissions = usePermissions();
  const roles = useRoles();
  const organizationRole = useOrganizationRole();
  const isOnboarded = useIsOnboarded();
  const teamsLoading = useTeamsLoading();
  const permissionsLoading = usePermissionsLoading();
  const hasHydrated = useHasHydrated();

  // Get error states
  const teamsError = useAuthStore((state) => state.teamsError);
  const permissionsError = useAuthStore((state) => state.permissionsError);

  // Filter and sort permissions
  const filteredPermissions = useMemo(() => {
    const entries = Object.entries(permissions).filter(
      ([key]) => key !== "_hasAllPermissions"
    );

    if (permissionSearch) {
      return entries.filter(([key]) =>
        key.toLowerCase().includes(permissionSearch.toLowerCase())
      );
    }

    return entries.sort((a, b) => a[0].localeCompare(b[0]));
  }, [permissions, permissionSearch]);

  const hasAllPermissions = permissions._hasAllPermissions === true;

  // Count granted/denied
  const grantedCount = filteredPermissions.filter(([, v]) => v === true).length;
  const deniedCount = filteredPermissions.filter(([, v]) => v === false).length;

  // Detect potential issues
  const issues: string[] = [];
  if (!hasHydrated) issues.push("Store not hydrated yet");
  if (!currentTeam && !teamsLoading) issues.push("No team selected");
  if (Object.keys(permissions).length === 0 && !permissionsLoading)
    issues.push("No permissions loaded");
  if (teamsError) issues.push(`Teams error: ${teamsError}`);
  if (permissionsError) issues.push(`Permissions error: ${permissionsError}`);

  const copyToClipboard = (data: object, label: string) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <ScrollArea className="h-[calc(80vh-180px)]">
      <div className="space-y-4 pr-4">
        {/* Issues Alert */}
        {issues.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-destructive font-medium mb-2">
              <AlertCircle className="h-4 w-4" />
              Potential Issues Detected
            </div>
            <ul className="text-sm text-destructive/80 space-y-1">
              {issues.map((issue, i) => (
                <li key={i}>• {issue}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Hydration Status */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Hydration Status</span>
          </div>
          <Badge variant={hasHydrated ? "default" : "destructive"}>
            {hasHydrated ? "Hydrated" : "Not Hydrated"}
          </Badge>
        </div>

        {/* Team Context Section */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-medium">Team Context</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                copyToClipboard(
                  { currentTeam, currentTeamId, teams },
                  "Team context"
                )
              }
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Team ID:</div>
            <div className="font-mono text-xs break-all">
              {currentTeamId || (
                <span className="text-destructive">None</span>
              )}
            </div>

            <div className="text-muted-foreground">Team Name:</div>
            <div>
              {currentTeam?.name || (
                <span className="text-destructive">None</span>
              )}
            </div>

            <div className="text-muted-foreground">Team Status:</div>
            <div>
              <Badge
                variant={
                  currentTeam?.status === "active" ? "default" : "secondary"
                }
              >
                {currentTeam?.status || "N/A"}
              </Badge>
            </div>

            <div className="text-muted-foreground">Available Teams:</div>
            <div>{teams.length}</div>

            <div className="text-muted-foreground">Loading:</div>
            <div className="flex items-center gap-1">
              {teamsLoading ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-yellow-600">Loading...</span>
                </>
              ) : (
                <span className="text-green-600">Idle</span>
              )}
            </div>
          </div>
        </div>

        {/* Roles Section */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <span className="font-medium">Roles</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                copyToClipboard({ roles, organizationRole }, "Roles")
              }
            >
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Organization Role:</div>
            <div>
              <Badge variant="outline">{organizationRole || "None"}</Badge>
            </div>

            <div className="text-muted-foreground">Team Roles:</div>
            <div className="flex flex-wrap gap-1">
              {roles.length > 0 ? (
                roles.map((role) => (
                  <Badge key={role.id} variant="secondary">
                    {role.name}
                  </Badge>
                ))
              ) : (
                <span className="text-destructive">None</span>
              )}
            </div>

            <div className="text-muted-foreground">Is Onboarded:</div>
            <div>
              <Badge variant={isOnboarded ? "default" : "secondary"}>
                {isOnboarded ? "Yes" : "No"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Permissions Section */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              <span className="font-medium">Permissions</span>
            </div>
            <div className="flex items-center gap-2">
              {permissionsLoading && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(permissions, "Permissions")}
              >
                <Copy className="h-3 w-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>

          {/* Special flags */}
          {hasAllPermissions && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-sm">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-medium">
                  Full Access (_hasAllPermissions: true)
                </span>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                User has owner/admin privileges - all permission checks return
                true
              </p>
            </div>
          )}

          {/* Stats */}
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-600" />
              <span>{grantedCount} granted</span>
            </div>
            <div className="flex items-center gap-1">
              <XCircle className="h-3 w-3 text-red-600" />
              <span>{deniedCount} denied</span>
            </div>
            <div className="text-muted-foreground">
              {filteredPermissions.length} total
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search permissions..."
              value={permissionSearch}
              onChange={(e) => setPermissionSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>

          {/* Permission list */}
          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {filteredPermissions.length === 0 ? (
              <div className="text-center text-muted-foreground py-4 text-sm">
                {permissionSearch
                  ? "No permissions match your search"
                  : "No permissions loaded"}
              </div>
            ) : (
              filteredPermissions.map(([key, value]) => (
                <div
                  key={key}
                  className={cn(
                    "flex items-center justify-between py-1.5 px-2 rounded text-sm",
                    value ? "bg-green-500/5" : "bg-red-500/5"
                  )}
                >
                  <span className="font-mono text-xs">{key}</span>
                  {value ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-600" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Teams List */}
        {teams.length > 0 && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-medium">Available Teams ({teams.length})</span>
            </div>

            <div className="space-y-2">
              {teams.map((membership) => (
                <div
                  key={membership.team.id}
                  className={cn(
                    "flex items-center justify-between p-2 rounded text-sm",
                    membership.team.id === currentTeamId
                      ? "bg-primary/10 border border-primary/30"
                      : "bg-muted/50"
                  )}
                >
                  <div>
                    <div className="font-medium">{membership.team.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {membership.team.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{membership.role.name}</Badge>
                    {membership.team.id === currentTeamId && (
                      <Badge variant="default">Active</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
